import { Badge } from "../../../../components/ui";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { buildThemeClasses } from "../../theme";
import {
  fetchBookings,
  fetchBlocks,
  getAvailability,
  fmtDateLong,
  fmtTime,
  BOOKING_STATUS_META,
  type Booking,
} from "../../services/bookingsService";

export { default as AgendaCalendar } from "./AgendaCalendar";
export { default as BloqueosSection } from "./BloqueosLive";
export { default as DisponibilidadSection } from "./DisponibilidadLive";

// ===========================================================================
// Agenda dashboard — live summary: upcoming sessions + quick counters. Deep
// links into the full calendar.
// ===========================================================================
export function AgendaDashboard() {
  const tc = buildThemeClasses();
  const navigate = useNavigate();

  const [bookings, setBookings] = useState<Booking[]>([]);
  const [blocksCount, setBlocksCount] = useState(0);
  const [tz, setTz] = useState("America/La_Paz");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [bk, bl, av] = await Promise.all([fetchBookings(), fetchBlocks(), getAvailability()]);
      setBookings(bk);
      setBlocksCount(bl.length);
      if (av) setTz(av.timezone);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const now = Date.now();
  const upcoming = useMemo(
    () =>
      bookings
        .filter((b) => b.status !== "cancelled" && new Date(b.start).getTime() >= now)
        .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime()),
    [bookings, now],
  );
  const requested = useMemo(() => bookings.filter((b) => b.status === "requested").length, [bookings]);
  const thisWeek = useMemo(() => {
    const weekEnd = now + 7 * 86400000;
    return upcoming.filter((b) => new Date(b.start).getTime() <= weekEnd).length;
  }, [upcoming, now]);

  const stats = [
    { label: "Próximas", value: upcoming.length, icon: "ri-calendar-check-line", tone: "text-primary-500" },
    { label: "Esta semana", value: thisWeek, icon: "ri-calendar-2-line", tone: "text-emerald-500" },
    { label: "Por confirmar", value: requested, icon: "ri-time-line", tone: "text-amber-500" },
    { label: "Bloqueos", value: blocksCount, icon: "ri-lock-line", tone: "text-gray-400" },
  ];

  return (
    <div className="h-full w-full overflow-y-auto p-6">
      <div className="mx-auto max-w-5xl">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-500/15 text-primary-500">
              <i className="ri-calendar-2-line text-xl" />
            </span>
            <div>
              <h1 className={`text-xl font-bold ${tc.text}`}>Agenda</h1>
              <p className={`text-sm ${tc.textSecondary}`}>Resumen de reservas y disponibilidad</p>
            </div>
          </div>
          <button
            onClick={() => navigate("/crm/agenda/calendario")}
            className="flex items-center gap-2 rounded-lg bg-primary-500 px-4 py-2 text-sm font-medium text-on-primary hover:bg-primary-400"
          >
            <i className="ri-calendar-2-line" /> Abrir calendario
          </button>
        </div>

        <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {stats.map((s) => (
            <div key={s.label} className={`rounded-xl border p-4 ${tc.cardBorder} ${tc.cardBg}`}>
              <div className="flex items-center justify-between">
                <span className={`text-2xl font-bold ${tc.text}`}>{loading ? "—" : s.value}</span>
                <i className={`${s.icon} text-xl ${s.tone}`} />
              </div>
              <p className={`mt-1 text-xs ${tc.textSecondary}`}>{s.label}</p>
            </div>
          ))}
        </div>

        <div className={`rounded-2xl border ${tc.cardBorder} ${tc.cardBg}`}>
          <div className={`flex items-center justify-between border-b p-4 ${tc.border}`}>
            <p className={`text-sm font-semibold ${tc.text}`}>Próximas sesiones</p>
          </div>
          {loading ? (
            <div className={`p-8 text-center text-sm ${tc.textSecondary}`}>
              <i className="ri-loader-4-line mr-2 animate-spin" /> Cargando...
            </div>
          ) : upcoming.length === 0 ? (
            <div className={`p-8 text-center text-sm ${tc.textSecondary}`}>
              <i className="ri-calendar-line mb-2 block text-3xl" /> No hay sesiones próximas.
            </div>
          ) : (
            <div className={`divide-y ${tc.border}`}>
              {upcoming.slice(0, 8).map((b) => (
                <div key={b.id} className="flex items-center justify-between gap-3 p-3">
                  <div className="flex items-center gap-3">
                    <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-500/10 text-primary-500">
                      <i className="ri-calendar-event-line" />
                    </span>
                    <div>
                      <p className={`text-sm font-medium ${tc.text}`}>{b.title || b.name || "Reserva"}</p>
                      <p className={`text-xs ${tc.textSecondary}`}>
                        {fmtDateLong(b.start, tz)} · {fmtTime(b.start, tz)}
                        {b.company ? ` · ${b.company}` : ""}
                      </p>
                    </div>
                  </div>
                  <Badge tone={BOOKING_STATUS_META[b.status].tone}>
                    {BOOKING_STATUS_META[b.status].label}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
